import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import { Save, Store, Link as LinkIcon, Truck, User, Image, FileText, Banknote, Plus, X, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSettings() {
  const { token, user } = useAuth();
  const [settings, setSettings] = useState({
    restaurant_name: '', tagline: '', description: '', logo_url: '',
    phone: '', whatsapp: '', email: '', address: '', instagram: '',
    delivery_fee: 500, delivery_zones: [{ zone: 'Nearby (0-5km)', fee: 500 }, { zone: 'Mid-range (5-15km)', fee: 1000 }, { zone: 'Far (15km+)', fee: 2000 }],
    min_order: 0, delivery_time: '30-45 min',
    about_text: '', about_text_2: '', about_image: '',
    gallery_images: [], opening_hours: '',
    bank_name: '', account_number: '', account_name: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newGalleryUrl, setNewGalleryUrl] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings`);
        setSettings(prev => ({ ...prev, ...res.data }));
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: name === 'delivery_fee' || name === 'min_order' ? parseFloat(value) || 0 : value
    }));
  };

  const addGalleryImage = () => {
    if (!newGalleryUrl.trim()) return;
    setSettings(prev => ({
      ...prev,
      gallery_images: [...(prev.gallery_images || []), newGalleryUrl.trim()]
    }));
    setNewGalleryUrl('');
  };

  const removeGalleryImage = (index) => {
    setSettings(prev => ({
      ...prev,
      gallery_images: prev.gallery_images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings, { headers });
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Settings" subtitle="Manage your restaurant settings">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-fire-500" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Settings" subtitle="Manage your restaurant settings">
      <div data-testid="admin-settings">
        <Tabs defaultValue="restaurant" className="space-y-6">
          <TabsList className="bg-card border border-white/10 flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="restaurant" className="data-[state=active]:bg-fire-500"><Store className="w-4 h-4 mr-1" /> Restaurant</TabsTrigger>
            <TabsTrigger value="contact" className="data-[state=active]:bg-fire-500"><LinkIcon className="w-4 h-4 mr-1" /> Contact</TabsTrigger>
            <TabsTrigger value="delivery" className="data-[state=active]:bg-fire-500"><Truck className="w-4 h-4 mr-1" /> Delivery</TabsTrigger>
            <TabsTrigger value="about" className="data-[state=active]:bg-fire-500"><FileText className="w-4 h-4 mr-1" /> About</TabsTrigger>
            <TabsTrigger value="gallery" className="data-[state=active]:bg-fire-500"><Image className="w-4 h-4 mr-1" /> Gallery</TabsTrigger>
            <TabsTrigger value="transfer" className="data-[state=active]:bg-fire-500"><Banknote className="w-4 h-4 mr-1" /> Transfer</TabsTrigger>
            <TabsTrigger value="account" className="data-[state=active]:bg-fire-500"><User className="w-4 h-4 mr-1" /> Account</TabsTrigger>
          </TabsList>

          <form onSubmit={handleSubmit}>
            {/* Restaurant Info */}
            <TabsContent value="restaurant">
              <div className="bg-card rounded-xl border border-white/10 p-6 space-y-6">
                <h3 className="text-white font-bold text-lg">Restaurant Information</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-gray-400">Restaurant Name</Label>
                    <Input name="restaurant_name" value={settings.restaurant_name} onChange={handleChange} className="bg-black/50 border-white/10 text-white mt-1" data-testid="settings-name" />
                  </div>
                  <div>
                    <Label className="text-gray-400">Tagline</Label>
                    <Input name="tagline" value={settings.tagline} onChange={handleChange} className="bg-black/50 border-white/10 text-white mt-1" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-gray-400">Description</Label>
                    <Textarea name="description" value={settings.description} onChange={handleChange} className="bg-black/50 border-white/10 text-white mt-1 min-h-[80px]" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-gray-400">Logo URL</Label>
                    <Input name="logo_url" value={settings.logo_url || ''} onChange={handleChange} className="bg-black/50 border-white/10 text-white mt-1" placeholder="https://..." />
                    {settings.logo_url && <img src={settings.logo_url} alt="Logo" className="h-12 mt-2" />}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Contact & Links */}
            <TabsContent value="contact">
              <div className="bg-card rounded-xl border border-white/10 p-6 space-y-6">
                <h3 className="text-white font-bold text-lg">Contact & Links</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-gray-400">Phone Number</Label>
                    <Input name="phone" value={settings.phone} onChange={handleChange} className="bg-black/50 border-white/10 text-white mt-1" data-testid="settings-phone" />
                  </div>
                  <div>
                    <Label className="text-gray-400">WhatsApp Number</Label>
                    <Input name="whatsapp" value={settings.whatsapp} onChange={handleChange} className="bg-black/50 border-white/10 text-white mt-1" />
                  </div>
                  <div>
                    <Label className="text-gray-400">Email</Label>
                    <Input name="email" type="email" value={settings.email} onChange={handleChange} className="bg-black/50 border-white/10 text-white mt-1" />
                  </div>
                  <div>
                    <Label className="text-gray-400">Instagram Handle</Label>
                    <Input name="instagram" value={settings.instagram || ''} onChange={handleChange} className="bg-black/50 border-white/10 text-white mt-1" placeholder="@sizzlebox" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-gray-400">Address</Label>
                    <Input name="address" value={settings.address} onChange={handleChange} className="bg-black/50 border-white/10 text-white mt-1" data-testid="settings-address" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-gray-400">Opening Hours</Label>
                    <Input name="opening_hours" value={settings.opening_hours || ''} onChange={handleChange} className="bg-black/50 border-white/10 text-white mt-1" placeholder="Mon-Sat: 10am - 10pm, Sun: 12pm - 8pm" data-testid="settings-hours" />
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Delivery */}
            <TabsContent value="delivery">
              <div className="bg-card rounded-xl border border-white/10 p-6 space-y-6">
                <h3 className="text-white font-bold text-lg">Delivery Settings</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-gray-400">Minimum Order (₦)</Label>
                    <Input name="min_order" type="number" value={settings.min_order} onChange={handleChange} className="bg-black/50 border-white/10 text-white mt-1" />
                  </div>
                  <div>
                    <Label className="text-gray-400">Delivery Time</Label>
                    <Input name="delivery_time" value={settings.delivery_time} onChange={handleChange} className="bg-black/50 border-white/10 text-white mt-1" />
                  </div>
                </div>

                <div>
                  <Label className="text-gray-400 mb-3 block">Delivery Zones & Fees</Label>
                  <div className="space-y-2">
                    {(settings.delivery_zones || []).map((zone, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <Input
                          value={zone.zone}
                          onChange={e => {
                            const zones = [...(settings.delivery_zones || [])];
                            zones[idx] = { ...zones[idx], zone: e.target.value };
                            setSettings(prev => ({ ...prev, delivery_zones: zones }));
                          }}
                          className="bg-black/50 border-white/10 text-white flex-1"
                          placeholder="Zone name (e.g. Nearby 0-5km)"
                        />
                        <Input
                          type="number"
                          value={zone.fee}
                          onChange={e => {
                            const zones = [...(settings.delivery_zones || [])];
                            zones[idx] = { ...zones[idx], fee: parseFloat(e.target.value) || 0 };
                            setSettings(prev => ({ ...prev, delivery_zones: zones }));
                          }}
                          className="bg-black/50 border-white/10 text-white w-28"
                          placeholder="Fee ₦"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          className="text-red-400 hover:text-red-500 px-2"
                          onClick={() => {
                            const zones = (settings.delivery_zones || []).filter((_, i) => i !== idx);
                            setSettings(prev => ({ ...prev, delivery_zones: zones }));
                          }}
                        >✕</Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      className="border-white/10 text-white hover:bg-white/10 w-full"
                      onClick={() => setSettings(prev => ({ ...prev, delivery_zones: [...(prev.delivery_zones || []), { zone: '', fee: 0 }] }))}
                    >+ Add Zone</Button>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* About */}
            <TabsContent value="about">
              <div className="bg-card rounded-xl border border-white/10 p-6 space-y-6">
                <h3 className="text-white font-bold text-lg">About Section</h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-gray-400">About Text (Paragraph 1)</Label>
                    <Textarea name="about_text" value={settings.about_text || ''} onChange={handleChange} className="bg-black/50 border-white/10 text-white mt-1 min-h-[100px]" data-testid="settings-about-text" />
                  </div>
                  <div>
                    <Label className="text-gray-400">About Text (Paragraph 2)</Label>
                    <Textarea name="about_text_2" value={settings.about_text_2 || ''} onChange={handleChange} className="bg-black/50 border-white/10 text-white mt-1 min-h-[80px]" />
                  </div>
                  <div>
                    <Label className="text-gray-400">About Image URL</Label>
                    <Input name="about_image" value={settings.about_image || ''} onChange={handleChange} className="bg-black/50 border-white/10 text-white mt-1" placeholder="https://..." />
                    {settings.about_image && <img src={settings.about_image} alt="About" className="h-32 rounded-lg mt-2 object-cover" />}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Gallery */}
            <TabsContent value="gallery">
              <div className="bg-card rounded-xl border border-white/10 p-6 space-y-6">
                <h3 className="text-white font-bold text-lg">Gallery Images</h3>
                <p className="text-gray-400 text-sm">Add image URLs to display in the homepage gallery</p>
                
                {/* Add new image */}
                <div className="flex gap-2">
                  <Input value={newGalleryUrl} onChange={e => setNewGalleryUrl(e.target.value)} placeholder="Paste image URL..." className="bg-black/50 border-white/10 text-white flex-1" data-testid="gallery-url-input" />
                  <Button type="button" onClick={addGalleryImage} className="bg-fire-500 hover:bg-fire-600" data-testid="add-gallery-btn">
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>

                {/* Current images */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {(settings.gallery_images || []).map((img, i) => (
                    <div key={i} className="relative group aspect-square rounded-lg overflow-hidden border border-white/10">
                      <img src={img} alt={`Gallery ${i + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeGalleryImage(i)}
                        className="absolute top-2 right-2 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`remove-gallery-${i}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                {(!settings.gallery_images || settings.gallery_images.length === 0) && (
                  <p className="text-gray-500 text-center py-8">No gallery images. Add some above!</p>
                )}
              </div>
            </TabsContent>

            {/* Bank Transfer */}
            <TabsContent value="transfer">
              <div className="bg-card rounded-xl border border-white/10 p-6 space-y-6">
                <h3 className="text-white font-bold text-lg">Bank Transfer Details</h3>
                <p className="text-gray-400 text-sm">These details will be shown to customers who choose bank transfer as payment</p>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <Label className="text-gray-400">Bank Name</Label>
                    <Input name="bank_name" value={settings.bank_name || ''} onChange={handleChange} className="bg-black/50 border-white/10 text-white mt-1" placeholder="GTBank" data-testid="settings-bank-name" />
                  </div>
                  <div>
                    <Label className="text-gray-400">Account Number</Label>
                    <Input name="account_number" value={settings.account_number || ''} onChange={handleChange} className="bg-black/50 border-white/10 text-white mt-1" placeholder="0123456789" data-testid="settings-account-number" />
                  </div>
                  <div>
                    <Label className="text-gray-400">Account Name</Label>
                    <Input name="account_name" value={settings.account_name || ''} onChange={handleChange} className="bg-black/50 border-white/10 text-white mt-1" placeholder="SizzleBox Ltd" data-testid="settings-account-name" />
                  </div>
                </div>
                {/* Preview */}
                {settings.bank_name && (
                  <div className="bg-black/50 rounded-lg p-4 border border-white/10">
                    <p className="text-gray-400 text-sm mb-2">Customer will see:</p>
                    <p className="text-white font-bold">{settings.bank_name}</p>
                    <p className="text-fire-500 font-mono text-xl">{settings.account_number}</p>
                    <p className="text-gray-300">{settings.account_name}</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Admin Account */}
            <TabsContent value="account">
              <div className="bg-card rounded-xl border border-white/10 p-6 space-y-6">
                <h3 className="text-white font-bold text-lg">Admin Account</h3>
                <div className="flex items-center gap-4 p-4 bg-black/50 rounded-lg">
                  <div className="w-16 h-16 rounded-full bg-fire-500/20 flex items-center justify-center">
                    <User className="w-8 h-8 text-fire-500" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg">{user?.name || 'Admin'}</p>
                    <p className="text-gray-400">{user?.email || 'admin@sizzlebox.ng'}</p>
                    <p className="text-gray-500 text-sm capitalize">Role: {user?.role || 'Admin'}</p>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Save Button */}
            <div className="mt-6 flex justify-end">
              <Button type="submit" disabled={saving} className="bg-fire-500 hover:bg-fire-600 text-white px-8" data-testid="save-settings">
                {saving ? 'Saving...' : <><Save className="w-4 h-4 mr-2" /> Save Changes</>}
              </Button>
            </div>
          </form>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
